import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ingredientesService, {
  IngredientesFilters,
  UpdateStockData,
  MovimientosFilters,
  SalidaBodegaPayload,
  EntradaBodegaPayload,
} from '../services/ingredientesService';
import type { Ingrediente } from '../types/ingredientes';

// Keys para las queries
export const ingredientesKeys = {
  all: ['ingredientes'] as const,
  lists: () => [...ingredientesKeys.all, 'list'] as const,
  list: (filters: IngredientesFilters) =>
    [...ingredientesKeys.lists(), filters] as const,
  details: () => [...ingredientesKeys.all, 'detail'] as const,
  detail: (id: number) => [...ingredientesKeys.details(), id] as const,
  categorias: () => [...ingredientesKeys.all, 'categorias'] as const,
  estadisticas: () => [...ingredientesKeys.all, 'estadisticas'] as const,
  stockBajo: () => [...ingredientesKeys.all, 'stock-bajo'] as const,
  movimientos: (id: number, filters: MovimientosFilters) =>
    [...ingredientesKeys.all, 'movimientos', id, filters] as const,
};

// Hook para obtener lista de ingredientes
export const useIngredientes = (filters: IngredientesFilters = {}) => {
  return useQuery({
    queryKey: ingredientesKeys.list(filters),
    queryFn: () => ingredientesService.getAll(filters),
    keepPreviousData: true,
  });
};

// Hook para obtener estadísticas generales de ingredientes
export const useIngredientesEstadisticas = () => {
  return useQuery({
    queryKey: ingredientesKeys.estadisticas(),
    queryFn: ingredientesService.getEstadisticas,
    staleTime: 30000, // Cache por 30 segundos
  });
};

// Hook para obtener un ingrediente específico
export const useIngrediente = (id: number) => {
  return useQuery({
    queryKey: ingredientesKeys.detail(id),
    queryFn: () => ingredientesService.getById(id),
    enabled: !!id,
  });
};

// Hook para obtener categorías
export const useCategorias = () => {
  return useQuery({
    queryKey: ingredientesKeys.categorias(),
    queryFn: ingredientesService.getCategorias,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
};

// Hook para obtener ingredientes con stock bajo
export const useIngredientesStockBajo = () => {
  return useQuery({
    queryKey: ingredientesKeys.stockBajo(),
    queryFn: ingredientesService.getStockBajo,
    refetchInterval: 30000, // Actualizar cada 30 segundos
  });
};

// Hook: lotes activos de un ingrediente (FIFO por vencimiento)
export const useLotesIngrediente = (id: number | null | undefined) => {
  return useQuery({
    queryKey: [...ingredientesKeys.all, 'lotes', id],
    queryFn: () => ingredientesService.getLotes(id!),
    enabled: typeof id === 'number' && id > 0,
    staleTime: 30 * 1000,
  });
};

// Hook: advertencias de migración / unidades no resueltas
export const useWarningsUnidades = () => {
  return useQuery({
    queryKey: [...ingredientesKeys.all, 'warnings-unidades'],
    queryFn: ingredientesService.getWarningsUnidades,
    staleTime: 60 * 1000,
  });
};

// Hook: vista de inventario de bodega (agregados por ingrediente)
export const useInventarioBodega = () => {
  return useQuery({
    queryKey: [...ingredientesKeys.all, 'inventario-bodega'],
    queryFn: ingredientesService.getInventarioBodega,
    staleTime: 30 * 1000,
  });
};

// Hook: marcar advertencia como resuelta
export const useResolverWarningUnidades = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (warningId: number) =>
      ingredientesService.resolverWarning(warningId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...ingredientesKeys.all, 'warnings-unidades'],
      });
    },
  });
};

// Hook para crear ingrediente
export const useCreateIngrediente = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Ingrediente>) =>
      ingredientesService.create(data),
    onSuccess: (newIngrediente) => {
      // Invalidar todas las listas de ingredientes
      queryClient.invalidateQueries({ queryKey: ingredientesKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: ingredientesKeys.categorias(),
      });
      queryClient.invalidateQueries({
        queryKey: ingredientesKeys.estadisticas(),
      });

      // Invalidar también todas las queries de ingredientes para asegurar actualización
      queryClient.invalidateQueries({ queryKey: ['ingredientes'] });

      console.log(
        `✅ Ingrediente "${newIngrediente.nombre}" creado exitosamente y cache invalidado`
      );
    },
    onError: (error: any) => {
      console.error('Error al crear ingrediente:', error);
    },
  });
};

// Hook para actualizar ingrediente
export const useUpdateIngrediente = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Ingrediente> }) =>
      ingredientesService.update(id, data),
    onSuccess: (updatedIngrediente, { id }) => {
      // Actualizar el cache del ingrediente específico
      queryClient.setQueryData(ingredientesKeys.detail(id), updatedIngrediente);

      // Invalidar listas para refrescar
      queryClient.invalidateQueries({ queryKey: ingredientesKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: ingredientesKeys.categorias(),
      });
      queryClient.invalidateQueries({
        queryKey: ingredientesKeys.estadisticas(),
      });

      console.log(
        `Ingrediente "${updatedIngrediente.nombre}" actualizado exitosamente`
      );
    },
    onError: (error: any) => {
      console.error('Error al actualizar ingrediente:', error);
    },
  });
};

// Hook para eliminar ingrediente
export const useDeleteIngrediente = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => ingredientesService.delete(id),
    onSuccess: () => {
      // Invalidar todas las queries relacionadas
      queryClient.invalidateQueries({ queryKey: ingredientesKeys.all });
      queryClient.invalidateQueries({
        queryKey: ingredientesKeys.estadisticas(),
      });
    },
  });
};

// Hook para actualizar stock
export const useUpdateStock = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      stockData,
    }: {
      id: number;
      stockData: UpdateStockData;
    }) => ingredientesService.updateStock(id, stockData),
    onSuccess: (updatedIngrediente, { id }) => {
      // Actualizar el cache del ingrediente específico
      queryClient.setQueryData(ingredientesKeys.detail(id), updatedIngrediente);

      // Invalidar listas para refrescar
      queryClient.invalidateQueries({ queryKey: ingredientesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ingredientesKeys.stockBajo() });
      queryClient.invalidateQueries({
        queryKey: ingredientesKeys.estadisticas(),
      });

      console.log(
        `Stock de "${updatedIngrediente.nombre}" actualizado exitosamente`
      );
    },
    onError: (error: any) => {
      console.error('Error al actualizar stock:', error);
    },
  });
};

// Hook para deshabilitar ingrediente
export const useDeshabilitarIngrediente = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => ingredientesService.deshabilitar(id),
    onSuccess: (updatedIngrediente) => {
      // Invalidar todas las queries de ingredientes para forzar refetch
      queryClient.invalidateQueries({ queryKey: ingredientesKeys.all });
      queryClient.invalidateQueries({
        queryKey: ingredientesKeys.estadisticas(),
      });

      // También remover el ingrediente específico del cache ya que ahora está inactivo
      queryClient.removeQueries({
        queryKey: ingredientesKeys.detail(Number(updatedIngrediente.id)),
      });

      console.log(
        `Ingrediente "${updatedIngrediente.nombre}" deshabilitado exitosamente`
      );
    },
    onError: (error: any) => {
      console.error('Error al deshabilitar ingrediente:', error);
    },
  });
};

// Hook para buscar ingredientes similares
export const useBuscarSimilares = (nombre: string) => {
  return useQuery({
    queryKey: ['ingredientes', 'similares', nombre],
    queryFn: () => ingredientesService.buscarSimilares(nombre),
    enabled: !!nombre && nombre.length > 2, // Solo buscar si tiene más de 2 caracteres
    staleTime: 1 * 60 * 1000, // 1 minuto
  });
};

// Hook para reactivar ingrediente
export const useReactivarIngrediente = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, stockActual }: { id: number; stockActual: number }) =>
      ingredientesService.reactivar(id, stockActual),
    onSuccess: (updatedIngrediente) => {
      // Actualizar el cache del ingrediente específico
      queryClient.setQueryData(
        ingredientesKeys.detail(Number(updatedIngrediente.id)),
        updatedIngrediente
      );

      // Invalidar listas para refrescar
      queryClient.invalidateQueries({ queryKey: ingredientesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ingredientesKeys.stockBajo() });
      queryClient.invalidateQueries({
        queryKey: ingredientesKeys.estadisticas(),
      });

      console.log(
        `Ingrediente "${updatedIngrediente.nombre}" reactivado exitosamente`
      );
    },
    onError: (error: any) => {
      console.error('Error al reactivar ingrediente:', error);
    },
  });
};

// Hook para ajustar stock manualmente
export const useAjustarStock = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      stock_nuevo,
      motivo,
      notas,
    }: {
      id: number;
      stock_nuevo: number;
      motivo: string;
      notas?: string;
    }) => ingredientesService.ajustarStock(id, { stock_nuevo, motivo, notas }),
    onSuccess: (result, { id }) => {
      // Actualizar el cache del ingrediente específico
      queryClient.setQueryData(ingredientesKeys.detail(id), result.ingrediente);

      // Invalidar listas para refrescar
      queryClient.invalidateQueries({ queryKey: ingredientesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ingredientesKeys.stockBajo() });
      queryClient.invalidateQueries({
        queryKey: ingredientesKeys.estadisticas(),
      });

      // Invalidar movimientos del ingrediente para refrescar el historial
      queryClient.invalidateQueries({
        queryKey: [...ingredientesKeys.all, 'movimientos', id],
      });

      console.log(
        `Stock de "${result.ingrediente.nombre}" ajustado exitosamente: ${result.movimiento.stock_anterior} → ${result.movimiento.stock_nuevo} (${result.movimiento.diferencia > 0 ? '+' : ''}${result.movimiento.diferencia})`
      );
    },
    onError: (error: any) => {
      console.error('Error al ajustar stock:', error);
    },
  });
};

// Hook para obtener historial de movimientos de stock
export const useMovimientosIngrediente = (
  id: number,
  filters: MovimientosFilters = {}
) => {
  return useQuery({
    queryKey: ingredientesKeys.movimientos(id, filters),
    queryFn: () => ingredientesService.getMovimientos(id, filters),
    enabled: !!id,
    keepPreviousData: true, // Mantener datos anteriores mientras carga nuevos (para paginación suave)
    staleTime: 10000, // Cache por 10 segundos
  });
};

// Hook: registrar una salida de bodega manual (PROMPT_N°046).
//
// Una salida toca stock, lotes y movimientos a la vez, así que invalida
// todo lo que muestra alguno de los tres: listas, estadísticas, stock bajo,
// los lotes del ingrediente y la vista de bodega.
export const useRegistrarSalidaBodega = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: SalidaBodegaPayload }) =>
      ingredientesService.registrarSalida(id, data),
    onSuccess: (result, { id }) => {
      queryClient.setQueryData(ingredientesKeys.detail(id), result.ingrediente);
      queryClient.invalidateQueries({ queryKey: ingredientesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ingredientesKeys.stockBajo() });
      queryClient.invalidateQueries({
        queryKey: ingredientesKeys.estadisticas(),
      });
      queryClient.invalidateQueries({
        queryKey: [...ingredientesKeys.all, 'movimientos', id],
      });
      queryClient.invalidateQueries({
        queryKey: [...ingredientesKeys.all, 'lotes', id],
      });
      queryClient.invalidateQueries({
        queryKey: [...ingredientesKeys.all, 'inventario-bodega'],
      });
    },
    onError: (error: any) => {
      console.error('Error al registrar salida de bodega:', error);
    },
  });
};

// Hook: deshacer una salida de bodega (PROMPT_N°051).
//
// Invalida lo mismo que la salida: el reverso toca stock, lotes y
// movimientos igual que ella, sólo que en el otro sentido.
export const useRevertirSalidaBodega = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      movimientoId,
      motivo,
    }: {
      id: number;
      movimientoId: number;
      motivo: string;
    }) => ingredientesService.revertirSalida(id, movimientoId, motivo),
    onSuccess: (result, { id }) => {
      queryClient.setQueryData(ingredientesKeys.detail(id), result.ingrediente);
      queryClient.invalidateQueries({ queryKey: ingredientesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ingredientesKeys.stockBajo() });
      queryClient.invalidateQueries({
        queryKey: ingredientesKeys.estadisticas(),
      });
      queryClient.invalidateQueries({
        queryKey: [...ingredientesKeys.all, 'movimientos', id],
      });
      queryClient.invalidateQueries({
        queryKey: [...ingredientesKeys.all, 'lotes', id],
      });
      queryClient.invalidateQueries({
        queryKey: [...ingredientesKeys.all, 'inventario-bodega'],
      });
    },
    onError: (error: any) => {
      console.error('Error al revertir la salida:', error);
    },
  });
};

// Hook: registrar una entrada de bodega manual (PROMPT_N°052).
//
// Invalida lo mismo que la salida, más la vista de bodega: una entrada
// crea un lote nuevo, así que la grilla de lotes también queda vieja.
export const useRegistrarEntradaBodega = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: EntradaBodegaPayload }) =>
      ingredientesService.registrarEntrada(id, data),
    onSuccess: (result, { id }) => {
      queryClient.setQueryData(ingredientesKeys.detail(id), result.ingrediente);
      queryClient.invalidateQueries({ queryKey: ingredientesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ingredientesKeys.stockBajo() });
      queryClient.invalidateQueries({
        queryKey: ingredientesKeys.estadisticas(),
      });
      queryClient.invalidateQueries({
        queryKey: [...ingredientesKeys.all, 'movimientos', id],
      });
      queryClient.invalidateQueries({
        queryKey: [...ingredientesKeys.all, 'lotes', id],
      });
      queryClient.invalidateQueries({
        queryKey: [...ingredientesKeys.all, 'inventario-bodega'],
      });
    },
    onError: (error: any) => {
      console.error('Error al registrar entrada de bodega:', error);
    },
  });
};
